// The user guide. One section per sidebar tab, in task-sized pieces, each step naming the
// exact on-screen labels in straight double quotes (scripts/check-guide.mjs verifies every
// quoted label still exists in the app). {client} is replaced with the program's short name.
// Roles: ADMIN is BlackWolf; EXECUTIVE the Steering Committee; CONTRIBUTOR the project team.
// Sections and tasks are filtered by role and capability exactly as the sidebar is.
import type { Role } from '../lib/types';
import type { GuideSection } from './types';

const BW: Role[] = ['ADMIN'];
const EXEC: Role[] = ['EXECUTIVE'];
const TEAM: Role[] = ['ADMIN', 'CONTRIBUTOR'];
const CLIENT: Role[] = ['EXECUTIVE', 'CONTRIBUTOR'];
const MENTION_D =
  'Type @ and pick a name to bring someone in: they get an email with a link to sign in and see it. The email never contains the comment itself.';

export const GUIDE: GuideSection[] = [
  // ─────────────────────────────── My work ───────────────────────────────
  {
    id: 'my-work',
    title: 'My work',
    purpose:
      'Your own open tasks, sorted by urgency, and any deliverable waiting for your acceptance. Contributors land here when they sign in.',
    tasks: [
      {
        title: 'Start your day',
        steps: [
          { t: 'Open "My work" in the sidebar.' },
          {
            t: 'Check "Awaiting your acceptance" first, if it shows.',
            d: 'These are deliverables submitted to {client} where you are the approver. The day count turns amber at 7 days and red at 14.',
          },
          {
            t: 'Work down the groups: "Overdue", "Blocked", "Due in the next 7 days", then "Everything else open".',
          },
          { t: 'Click a task to open its panel, or a deliverable to open its page.' },
        ],
      },
      {
        title: 'Ask someone a question',
        steps: [
          { t: 'Open the task, deliverable or RAID item the question is about.' },
          { t: 'In the comment box, type @ and pick the person from the list.', d: MENTION_D },
          { t: 'Write the question and press "Comment".' },
          {
            t: 'When you are mentioned, follow the link in the email and reply in the same comments.',
            where: 'outside',
          },
        ],
      },
      {
        title: 'Update a task you own',
        roles: ['CONTRIBUTOR'],
        steps: [
          { t: 'Click the task to open its panel.' },
          {
            t: 'Set "Status" (for example "In progress" or "Done") and drag "Progress" to the percent complete.',
            d: 'Setting Done also sets progress to 100%. Each change shows "Undo" for a few seconds.',
          },
          { t: 'Tick items under "Checklist", or add one in "Add an item".' },
          {
            t: 'To change dates, owner or priority, write a comment and press "Comment". Mention BlackWolf with @ so they see it.',
            d: 'BlackWolf sets dates, owners and priority. Your comment asks for the change.',
          },
          {
            t: 'File supporting work under "Attachments": drop a file on the list, or click "Drop a file here or click to file one".',
            d: 'You can file on tasks you own. Tick the confirmation that the file holds no borrower, loan-level or personal information, then "File it".',
          },
        ],
      },
      {
        title: 'Update a task',
        roles: BW,
        steps: [
          { t: 'Click the task to open its panel.' },
          {
            t: 'Change "Status", "Owner", "Dates", "Progress", "Priority", "Phase" or "Deliverable" in place.',
            d: 'Each change saves at once and shows "Undo".',
          },
          { t: 'Use "Dependencies" to record the tasks that block this one.' },
        ],
      },
    ],
  },

  // ─────────────────────────────── Executive ───────────────────────────────
  {
    id: 'executive',
    title: 'Executive',
    purpose:
      'The program at a glance before a Steering Committee meeting: health, the phases, what needs the committee, the timeline against the plan, risks, spend and success criteria. Executives land here when they sign in.',
    tasks: [
      {
        title: 'Read the program in two minutes',
        steps: [
          {
            t: 'Read the health banner at the top.',
            d: 'Green, amber or red overall health with BlackWolf’s one-line status, and how many PA §4 deliverables are accepted.',
          },
          { t: 'Scan the phase cards; click one to open that phase.' },
          {
            t: 'Check the two cards under the phases: decisions pending, and deliverables awaiting {client} review.',
            d: 'They turn amber while something waits and red once a decision is past its date or a review has waited 14 days. Click either to see the list.',
          },
          {
            t: 'Read "Timeline against the PA plan".',
            d: 'Grey is the plan, the coloured bar is the forecast coloured by health, diamonds are milestones and the dashed line is today. The right column shows "On plan" or how many days late. Hover a bar for its dates.',
          },
          {
            t: 'Read "Risks and issues": the heat map of open risks and the counts by severity. "RAID log" opens the detail.',
          },
          {
            t: 'Read "Implementation spend": approved against committed and actual.',
            requires: 'budget.read',
          },
          { t: 'Check the "Success criteria" bar for how many targets are achieved, on track or at risk.' },
        ],
      },
      {
        title: 'Open the latest weekly report',
        steps: [
          {
            t: 'Click "Weekly report" in the health banner.',
            d: 'It opens the latest published weekly status report.',
          },
        ],
      },
      {
        title: 'Update the one-line status',
        roles: ['ADMIN'],
        steps: [
          { t: 'Click the pencil beside the status in the health banner.' },
          { t: 'Type one line for the committee (280 characters at most, no em dashes) and press "Save".' },
        ],
      },
      {
        title: 'Ask the assistant for a progress review',
        steps: [
          {
            t: 'Click "Review program progress".',
            d: 'Until {client}’s written approval is recorded the button says it is awaiting approval and nothing is sent.',
          },
          {
            t: 'Read each section: "Slippage", "Stale tasks", "Inconsistent dates", "Deliverables with no tasks", "Risks with no mitigation" and "Focus this week".',
            d: 'It is a draft to check against the plan. Nothing in HELM changes.',
          },
        ],
      },
    ],
  },

  // ─────────────────────────────── Tracker ───────────────────────────────
  {
    id: 'tracker',
    title: 'Tracker',
    purpose:
      'The plan: phases on a to-scale roadmap, every task on a board or in a list, and a page per phase. BlackWolf owns the plan; contributors update the tasks they own; executives read and comment.',
    tasks: [
      {
        title: 'See the roadmap',
        steps: [
          { t: 'Open "Tracker". The "Roadmap" view opens first.' },
          {
            t: 'Read each row: phase health, tasks done and deliverables accepted, with the forecast bar against the grey PA window.',
          },
          { t: 'Switch "Month" and "Quarter" to zoom.' },
          { t: 'Click a phase name or its bar to open the phase page.' },
        ],
      },
      {
        title: 'Reforecast a phase',
        roles: BW,
        steps: [
          {
            t: 'On the roadmap, drag either end of a phase’s forecast bar to the new date.',
            d: 'Or focus the bar end and use the arrow keys (Shift moves a week), then Enter. Escape cancels. The change shows "Undo".',
          },
          {
            t: 'Or open the phase page and click "Reforecast" on the dates card, set "Forecast start" and "Forecast end", then "Save".',
          },
        ],
      },
      {
        title: 'Find and filter tasks',
        steps: [
          { t: 'Choose "Board" or "List".' },
          {
            t: 'Filter with the phase, owner and due date selects ("Me" shows your own tasks; "Overdue" shows late ones).',
          },
          {
            t: 'Save a filter set: click the bookmark beside the saved views, name it, then "Save". Pick it later from "Saved views…".',
            d: 'Saved views are yours alone.',
          },
          { t: 'Press Ctrl K anywhere to jump straight to a phase, task, deliverable or document.' },
        ],
      },
      {
        title: 'Move work on the board',
        roles: TEAM,
        steps: [
          {
            t: 'On "Board", drag a card to another column to change its status.',
            d: 'Columns are "Not started", "In progress", "Blocked", "In review" and "Done". Contributors can move the cards they own. A move shows "Undo".',
          },
          { t: 'Click a card to open its task panel.' },
        ],
      },
      {
        title: 'Add a task',
        roles: BW,
        steps: [
          { t: 'Press N anywhere, or click "Task" on the Tracker.' },
          {
            t: 'Type the "Title", choose the "Phase", then "Add task".',
            d: 'Shift Enter adds it and opens its panel.',
          },
          { t: 'In the panel, set "Owner", "Dates" and "Priority", and link the "Deliverable" it produces.' },
        ],
      },
      {
        title: 'Delete a task',
        roles: BW,
        steps: [
          { t: 'Open the task and click "Delete task" (the bin icon at the top of the panel), then "Delete".' },
          { t: 'Changed your mind? Restore it from "Recently deleted" in Admin within 30 days.' },
        ],
      },
      {
        title: 'Edit many tasks at once',
        roles: BW,
        steps: [
          { t: 'On "List", tick the tasks.' },
          {
            t: 'Use "Set status…", "Set owner…", "Set priority…" or the due date in the bar that appears, then "Clear selection".',
          },
        ],
      },
      {
        title: 'Run a phase',
        roles: BW,
        steps: [
          { t: 'Open the phase from the roadmap.' },
          {
            t: 'If health needs explaining, click "Override", choose the "Health", write one line in "Reason", then "Set health".',
            d: 'The committee sees your reason next to what the rules compute. "Clear override" removes it.',
          },
          { t: 'Add milestones with "Add" on the milestones card; tick one to mark it achieved today.' },
          {
            t: 'To change a milestone, click its pencil. In "Edit milestone" change the "Name" or "Forecast date", or set "Status".',
            d: 'Achieved asks for "Achieved on" (today or earlier). Cancelled keeps it on record, struck through, and stops it counting toward health. Set it back to Planned to reinstate it. The PA planned date never changes.',
          },
          { t: 'Keep running notes on the "Notes" tab; they save as you type.' },
        ],
      },
    ],
    notes: [
      {
        text: 'A phase turns red when a milestone is past its forecast date, a critical issue is open against it, or more than a quarter of its tasks are overdue; amber when any task is overdue or the forecast runs past the PA window.',
      },
      {
        text: 'Contributors update status, progress, checklist and description on tasks they own. Dates, owners and priority are set by BlackWolf: comment to ask.',
        roles: ['ADMIN', 'CONTRIBUTOR'],
      },
    ],
  },

  // ─────────────────────────────── Deliverables ───────────────────────────────
  {
    id: 'deliverables',
    title: 'Deliverables',
    purpose:
      'The register of what the Project Assignment requires and the formal record of each submission and acceptance (Agreement §3).',
    tasks: [
      {
        title: 'Find a deliverable',
        steps: [
          { t: 'Open "Deliverables". Use "Show" to narrow the list, for example to "Awaiting my acceptance".' },
          {
            t: 'Click a name to open its page: status, the document and its versions, comments and the submission log.',
          },
        ],
      },
      {
        title: 'Review and accept a deliverable',
        roles: EXEC,
        steps: [
          {
            t: 'Open it from "My work" (under "Awaiting your acceptance") or from the register.',
            d: 'You can accept only where you are the designated approver.',
          },
          { t: 'Preview or download the current version under "Document".' },
          { t: 'Read it against the description and the review checklist.', where: 'outside' },
          {
            t: 'Click "Accept", or click "Ask for revisions", write "What needs to change", then "Send to BlackWolf".',
            d: 'Acceptance is recorded with your name and the date. Asking for revisions notifies BlackWolf.',
          },
        ],
      },
      {
        title: 'Comment on a deliverable',
        steps: [{ t: 'Write in "Comments" on the deliverable page and press "Comment".', d: MENTION_D }],
      },
      {
        title: 'Draft, file and submit a deliverable',
        roles: BW,
        steps: [
          { t: 'Open the deliverable and click "Start drafting".' },
          { t: 'Write the document.', where: 'outside' },
          {
            t: 'File it: click "Drop a file here or click to file one" under "Document", tick the confirmation, then "File it".',
            d: 'Every upload becomes the next version; nothing is deleted.',
          },
          {
            t: 'Optionally "Send to internal review", and use "Review the draft" for the assistant’s check against the checklist.',
            d: 'Post useful findings with "Post as comment"; they carry an AI-assisted badge.',
          },
          {
            t: 'Click "Submit for review".',
            d: 'The approver is notified and the review clock starts. While submitted the document is locked; "Withdraw submission" reopens it with a note.',
          },
          {
            t: 'If {client} asks for revisions, revise, file the new version and submit again.',
            d: 'Use "Compare versions" to summarise what changed for the approver.',
          },
        ],
      },
      {
        title: 'Set the approver, target date and checklist',
        roles: BW,
        steps: [
          { t: 'Under "Details", set the "Target" date.' },
          {
            t: 'Leave "Approver" on the default approver, or delegate to another executive.',
            d: 'If nobody can accept, link the approver seat to a login in Admin under Steering Committee.',
          },
          { t: 'Under "Review checklist", click "Edit", add rows with "Add item", then "Save checklist".' },
        ],
      },
    ],
    notes: [
      { text: 'Review days turn amber at 7 and red at 14. Resubmitting restarts the clock.' },
      {
        text: 'Accepted deliverables are final. A submitted deliverable cannot take a new version until it is withdrawn or sent back.',
      },
    ],
  },

  // ─────────────────────────────── Documents ───────────────────────────────
  {
    id: 'documents',
    title: 'Documents',
    purpose:
      'Every file in the program, tied to its phase, task, deliverable, meeting or vendor. New uploads become versions and every version is kept.',
    tasks: [
      {
        title: 'Find and open a file',
        steps: [
          {
            t: 'Open "Documents" and narrow the list with "Search titles and tags", the phase and type selects, or "Awaiting {client} review".',
          },
          {
            t: 'Click a title to preview it (PDF and images) or download it. The arrow icon opens the document’s own page.',
            d: 'Links last five minutes and every view or download is logged.',
          },
          { t: 'Use the history icon to see and download earlier versions.' },
        ],
      },
      {
        title: 'File a document',
        roles: TEAM,
        steps: [
          {
            t: 'Remove any borrower, loan-level or personal information from the file first.',
            where: 'outside',
            d: 'HELM holds program artifacts only.',
          },
          {
            t: 'BlackWolf: click "File a document". Contributors: open a task you own and drop the file on its "Attachments".',
          },
          {
            t: 'Choose the file, check "Title", pick the "Type" and add "Tags".',
            d: 'PDF, Word, Excel, PowerPoint, CSV, text, Markdown, PNG and JPG, up to 50 MB.',
          },
          { t: 'Tick the confirmation and click "File it".' },
          {
            t: 'If HELM flags borrower or loan-level data, remove it and upload again.',
            d: 'Spreadsheets, CSV, text and Markdown are screened for loan and borrower columns and SSN-shaped values.',
          },
          {
            t: 'To add a version later, use the upload icon on the file’s row.',
            d: 'Only the person who filed it, or BlackWolf, can add a version.',
          },
        ],
      },
      {
        title: 'Delete a document filed by mistake',
        roles: BW,
        steps: [
          { t: 'Click the bin icon on the file’s row, then "Delete".' },
          {
            t: 'If HELM says the file is in use, unlink it where it says first.',
            d: 'Files serving as the signed AI approval letter, success-criteria evidence, meeting minutes, or an expense approval or receipt cannot be deleted. Published reports and the document of a submitted or accepted deliverable never can.',
          },
          { t: 'Restore it from "Recently deleted" in Admin within 30 days if needed.' },
        ],
      },
      {
        title: 'Pull key terms out of an agreement',
        roles: BW,
        steps: [
          { t: 'File the agreement, then open its page with the arrow icon on its row.' },
          {
            t: 'Click "Extract key terms".',
            d: 'Client-provided documents need Tier 3 in the recorded AI approval.',
          },
          { t: 'Check every row against the agreement; edit, remove or "Add a row".' },
          { t: 'Name it in "Working paper title" and click "File as working paper".' },
        ],
      },
    ],
    notes: [{ text: 'Executives read and download; they do not file documents.', roles: ['EXECUTIVE'] }],
  },

  // ─────────────────────────────── RAID ───────────────────────────────
  {
    id: 'raid',
    title: 'RAID',
    purpose:
      'One log of risks, assumptions, issues, decisions and dependencies, with a heat map of open risks. Items open in a side panel.',
    tasks: [
      {
        title: 'Read the log',
        steps: [
          { t: 'Open "RAID". Pick a type tab ("Risks", "Issues", "Decisions" and so on).' },
          {
            t: 'Click a heat map cell to list the open risks with that likelihood and impact.',
            d: 'Score is likelihood times impact: 1 to 6 low, 8 to 12 medium, 15 to 25 high.',
          },
          { t: 'Use the status and phase selects to filter; click a row to open it.' },
        ],
      },
      {
        title: 'Raise a risk, issue or other item',
        roles: TEAM,
        steps: [
          { t: 'Click "Raise item" and choose the type.' },
          {
            t: 'Fill in "Title" and "Description". For a risk set likelihood and impact; for an issue set "Severity".',
          },
          { t: 'Choose the "Phase" (or "Program-wide") and a target date.' },
          { t: 'Click the raise button. The item’s panel opens.' },
        ],
      },
      {
        title: 'Update an item',
        roles: TEAM,
        steps: [
          { t: 'Open the item from the log.' },
          {
            t: 'Edit the fields you need, then "Save changes".',
            d: 'Contributors edit the title, description, mitigation or action plan, resolution and target date on items they raised. Status, owner and scoring are set by BlackWolf.',
          },
          { t: 'Comment to ask for anything else, then press "Comment".', d: MENTION_D },
        ],
      },
      {
        title: 'Record a decision',
        roles: ['ADMIN', 'EXECUTIVE'],
        steps: [
          { t: 'Open the decision from "Decisions" (or the decisions card on Executive).' },
          {
            t: 'Fill in "Choice", "Decided by" and "Rationale", then "Record decision".',
            d: 'Recording closes the item with your choice on the record.',
          },
          {
            t: 'If the decision was taken in a meeting, record it here afterwards.',
            where: 'outside',
          },
        ],
      },
      {
        title: 'Delete an item raised by mistake',
        roles: BW,
        steps: [
          { t: 'Open the item and click "Delete item" (the bin icon), then confirm.' },
          { t: 'Restore it from "Recently deleted" in Admin within 30 days if needed.' },
        ],
      },
      {
        title: 'Get suggested risks for a phase',
        roles: TEAM,
        steps: [
          { t: 'Click "Suggest risks", choose a phase, then "Suggest".' },
          { t: '"Accept" the ones that fit and "Discard" the rest.', d: 'Accepted risks carry an AI-assisted badge.' },
        ],
      },
    ],
    notes: [
      { text: 'Executives read the log, comment and record decisions; they do not raise items.', roles: ['EXECUTIVE'] },
      { text: 'An open critical issue against a phase turns that phase red.' },
    ],
  },

  // ─────────────────────────────── Reports ───────────────────────────────
  {
    id: 'reports',
    title: 'Reports',
    purpose:
      'The PA §6 reporting: weekly status, monthly executive report, risk and issue log, budget tracking and Quarterly Board materials. Each is generated from HELM’s data, narrated by BlackWolf and frozen when published.',
    tasks: [
      {
        title: 'Read a published report',
        steps: [
          { t: 'Open "Reports" and click a report under its type.' },
          {
            t: 'Read it in the page, or use "Download PDF". The risk log and budget report also offer "Download spreadsheet"; board materials offer "Download PowerPoint".',
          },
          {
            t: 'A corrected report shows the correction note; older versions show as "Superseded".',
          },
        ],
      },
      {
        title: 'Publish the weekly status report',
        roles: BW,
        steps: [
          { t: 'Bring the Tracker and RAID log up to date for the week.' },
          { t: 'In "Reports", choose the week and click "Generate weekly report".' },
          {
            t: 'Write the "Executive summary", the "Asks of the Steering Committee" and the "Highlights" ("Add highlight" for more).',
            d: 'The preview on the right updates as you type. "Draft with the assistant" offers a starting point once approved.',
          },
          { t: 'If the data moved since, click "Refresh data". Your narrative stays.' },
          {
            t: 'Click "Publish", keep "Notify the Steering Committee" ticked, then "Publish".',
            d: 'Executives get a sign-in link, never the report itself. Published reports cannot change.',
          },
        ],
      },
      {
        title: 'Publish the monthly executive report',
        roles: BW,
        steps: [
          { t: 'Choose the month and click "Generate monthly report".' },
          { t: 'Write the "Executive summary", the asks, "A line per phase" and "Next month\'s focus".' },
          { t: 'Click "Publish", then "Publish".' },
        ],
      },
      {
        title: 'Publish the risk and issue log',
        roles: BW,
        steps: [
          { t: 'Click "Generate the log".', d: 'It is taken as of today and stamped Risk Register, Version N.' },
          { t: 'Add "Commentary" if useful (optional), then "Publish".' },
          { t: 'Use "Download spreadsheet" for the Excel version.' },
        ],
      },
      {
        title: 'Publish the budget tracking report',
        roles: BW,
        requires: 'budget.read',
        steps: [
          { t: 'Record the month’s committed and actual spend in Budget first.' },
          { t: 'Choose the month and click "Generate budget report".' },
          { t: 'Write the "Commentary" (required), then "Publish".' },
        ],
      },
      {
        title: 'Prepare the Quarterly Board materials',
        roles: BW,
        requires: 'budget.read',
        steps: [
          { t: 'Choose the quarter and click "Generate board materials".' },
          {
            t: 'Write the "Overall message" and "A line per slide".',
            d: 'Each line prints under its slide title and becomes the speaker note in the PowerPoint.',
          },
          { t: 'Click "Download PowerPoint" and drop the five slides into {client}’s board deck.', where: 'outside' },
          { t: 'Click "Publish" to file the PDF.' },
        ],
      },
      {
        title: 'Correct a published report',
        roles: BW,
        steps: [
          { t: 'Open the published report and click "Issue a correction".' },
          { t: 'Say "What does the correction change?" (at least 10 characters), then "Open correction draft".' },
          {
            t: 'Fix the narrative, then publish as usual.',
            d: 'It becomes the next version; the original stays on record.',
          },
        ],
      },
    ],
    notes: [
      { text: 'Em dashes are not allowed in reports; HELM asks you to rewrite before publishing.', roles: ['ADMIN'] },
      {
        text: 'Budget tracking reports and board materials are visible only to members with budget access.',
        roles: ['ADMIN', 'EXECUTIVE'],
      },
    ],
  },

  // ─────────────────────────────── Meetings ───────────────────────────────
  {
    id: 'meetings',
    title: 'Meetings',
    purpose:
      'Steering Committee meetings, working sessions and vendor demonstrations. Action items become tasks; decisions go to the RAID log.',
    tasks: [
      {
        title: 'Find a meeting and its follow-ups',
        steps: [
          {
            t: 'Open "Meetings". Use the calendar, "Upcoming" or "Recent".',
            d: 'Dashed entries are expected meetings; missed ones are flagged.',
          },
          {
            t: 'Open a meeting for its agenda, minutes, attendees, action items and decisions.',
            d: 'Action items appear in the owner’s My work; decisions open in the RAID log.',
          },
        ],
      },
      {
        title: 'Set the meeting rhythm',
        roles: BW,
        steps: [
          { t: 'Click "Cadence", then "Add a cadence" for each standing meeting.' },
          { t: 'Set the meeting type, "Frequency", "Which week" and "Day", then "Save".' },
        ],
      },
      {
        title: 'Record a meeting',
        roles: BW,
        steps: [
          { t: 'Click "New meeting", set "Type", "Title", "Date and time" and the "Agenda", then "Create".' },
          { t: 'Hold the meeting.', where: 'outside' },
          { t: 'Click "Mark held". Use "Edit" to add attendees ("Add a member", or a "Guest").' },
          {
            t: 'Under "Action items" click "Add": "What needs doing", "Owner", "Due" and "Phase", then "Add".',
            d: 'It becomes a task in the owner’s My work.',
          },
          {
            t: 'Under "Decisions" click "Add": "Decision needed", "Options", "Needed by", then "Add".',
            d: 'It goes to the RAID log for the committee to record the outcome.',
          },
        ],
      },
      {
        title: 'File minutes and extract follow-ups',
        roles: BW,
        steps: [
          { t: 'Write the minutes.', where: 'outside' },
          { t: 'Click "File minutes", tick the confirmation, then "File it".' },
          {
            t: 'Click "Extract from minutes", check each proposal, then "Add action item", "Add decision" or "Dismiss".',
            d: 'Minutes {client} wrote are Tier 3 under the AI approval.',
          },
        ],
      },
      {
        title: 'Cancel or delete a meeting',
        roles: BW,
        steps: [
          {
            t: 'If it did not happen, open it, click "Edit", set "Status" to Cancelled, then "Save".',
            d: 'A cancelled meeting stays on the calendar, struck through.',
          },
          {
            t: 'If it was created by mistake, click "Delete" beside "Edit", then "Delete" again to confirm.',
            d: 'Only meetings with no action items or decisions can be deleted. It can be restored from "Recently deleted" in Admin for 30 days.',
          },
        ],
      },
    ],
  },

  // ─────────────────────────────── Success Criteria ───────────────────────────────
  {
    id: 'criteria',
    title: 'Success Criteria',
    purpose:
      'The PA §5 targets with their status, current value and evidence. Most become measurable from the pilot in Phase 6.',
    tasks: [
      {
        title: 'Read the criteria',
        steps: [
          { t: 'Open "Success Criteria". Each row shows the target, when it becomes measurable and its status.' },
          { t: 'Click "Evidence" to open the supporting document.' },
          { t: 'Click "Show history" to see every change with its note.' },
        ],
      },
      {
        title: 'Update a criterion',
        roles: BW,
        steps: [
          { t: 'Get the measurement from {client}’s reporting, as an aggregate figure only.', where: 'outside' },
          { t: 'File the supporting report in Documents.' },
          { t: 'Click the pencil on the criterion.' },
          {
            t: 'Set "Status", the "Current value" and "Evidence", and write the "Note".',
            d: 'Measuring before the criterion’s phase starts asks "Why measure it now?".',
          },
          { t: 'Click "Save". The change joins the history.' },
        ],
      },
    ],
  },

  // ─────────────────────────────── Budget ───────────────────────────────
  {
    id: 'budget',
    title: 'Budget',
    purpose:
      '{client}’s implementation spend by category: approved against committed and actual. Implementation spend only. Visible to BlackWolf and executives with budget access.',
    tasks: [
      {
        title: 'Read the budget',
        steps: [
          {
            t: 'Read the "Approved", "Committed", "Actual" and "Headroom" tiles.',
            d: 'Headroom turns red when committed exceeds approved.',
          },
          { t: 'Click "Show months" on a category for its month-by-month spend.' },
          { t: 'Check "Approval history" for what was approved, when and on what basis.' },
        ],
      },
      {
        title: 'Set an approved amount',
        roles: BW,
        steps: [
          { t: 'Get the Implementation Budget Estimate accepted by {client}.', where: 'outside' },
          { t: 'Click the approved amount on the category row.' },
          {
            t: 'Enter "Approved (USD)" and cite the approval in "Source", then "Save".',
            d: 'The citation is required and listed in the approval history.',
          },
        ],
      },
      {
        title: 'Record a month of spend',
        roles: BW,
        steps: [
          { t: 'Get the month’s committed and paid amounts from {client} finance.', where: 'outside' },
          { t: 'Click "Record a month" on the category.' },
          {
            t: 'Set "Month", "Committed" and "Actual", add a "Note", then "Save".',
            d: 'Committed is contracted but not yet paid; actual is paid. Saving a month again replaces its totals.',
          },
        ],
      },
    ],
  },

  // ─────────────────────────────── Vendors ───────────────────────────────
  {
    id: 'vendors',
    title: 'Vendors',
    purpose:
      'The Phase 2 vendor evaluation across the technology categories. Scores stay hidden from other evaluators until each person submits.',
    tasks: [
      {
        title: 'Score a category as an evaluator',
        steps: [
          { t: 'Open "Vendors" and the category marked "Your scores are due".' },
          {
            t: 'On "My scores", pick 1 (poor) to 5 (excellent) for every vendor on every criterion.',
            d: 'Scores save as you go and nobody sees them until you submit.',
          },
          {
            t: 'Optionally click "Suggest" above a vendor for scores drawn from its proposal, with the passages behind them.',
            d: 'A suggestion counts only when you use it. Check any passage marked as not found in the documents.',
          },
          { t: 'Click "Submit my scores", then "Submit". Your scores lock and the comparison opens.' },
        ],
      },
      {
        title: 'Read the comparison',
        steps: [
          {
            t: 'Open "Comparison".',
            d: 'Evaluators see it after submitting; executives once anyone has submitted.',
          },
          {
            t: 'Move the weight sliders to test a what-if; "Reset" restores the agreed weights.',
            d: 'Moving sliders saves nothing.',
          },
          { t: 'Set "Years of ownership" and "Loans serviced (total count)" for the cost column.' },
          { t: 'Click "Export the matrix" for the spreadsheet (the Vendor Evaluation Matrix).' },
        ],
      },
      {
        title: 'Set up a category',
        roles: BW,
        steps: [
          {
            t: 'On "Setup", add criteria with "Add criterion", name each and set "Weight percent" to total 100, then "Save criteria".',
          },
          {
            t: 'Tick the evaluators (BlackWolf and the {client} people named for the category) and "Save evaluators".',
          },
          {
            t: 'On "Vendors", click "Add vendor": "Name", "Status", contact, "Website", "Notes" and the "Proposal costs".',
          },
          { t: 'Collect each vendor’s proposal and hold the demonstration.', where: 'outside' },
          { t: 'Use "File material" on the vendor to file the proposal and demo notes.' },
        ],
      },
      {
        title: 'Remove a vendor',
        roles: BW,
        steps: [
          {
            t: 'Once evaluators have scored it, click "Edit" on the vendor and set "Status" to Declined, then "Save".',
            d: 'It drops out of scoring and stays in the comparison marked declined, so submitted scores do not change.',
          },
          {
            t: 'If no one has scored it yet, click "Edit", then "Delete vendor", then "Delete".',
            d: 'Restore it from "Recently deleted" in Admin within 30 days if needed.',
          },
        ],
      },
      {
        title: 'Agree weights and reopen scores',
        roles: BW,
        steps: [
          { t: 'Agree final weights with {client}, set them on "Comparison" and click "Save these weights".' },
          {
            t: 'If an evaluator needs to change a submitted score, click "Reopen scores" beside them on "Setup", give a reason, then "Reopen".',
          },
        ],
      },
    ],
  },

  // ─────────────────────────────── Work Record ───────────────────────────────
  {
    id: 'work-record',
    title: 'Work Record',
    purpose:
      'BlackWolf only. The record of work performed, expense pre-approvals and invoice dates. Amounts stay in the accounting system.',
    tasks: [
      {
        title: 'Close the month',
        steps: [
          {
            t: 'Choose the "Month" and click "Compile from activity".',
            d: 'It pulls in BlackWolf’s tasks, filed documents, submissions, meetings and reports.',
          },
          { t: 'Untick anything that should not print; click "Add entry" for work done outside HELM.' },
          { t: 'Click "Export PDF". The record is filed in Documents as a new version.' },
        ],
      },
      {
        title: 'Record an invoice',
        steps: [
          { t: 'Send the invoice from the accounting system.', where: 'outside' },
          { t: 'Click "Record invoice": "Invoice number", "Service month", "Invoice date", then "Record".' },
          { t: 'When paid, click "Mark paid today".' },
        ],
      },
      {
        title: 'Pre-approve an expense',
        steps: [
          { t: 'Get {client}’s written approval before incurring it, and file it in Documents.', where: 'outside' },
          {
            t: 'Click "Request", describe it and enter the amount, set "Status" to Approved, and pick the "Written approval".',
          },
          { t: 'After reimbursement, set it to Reimbursed and attach the "Receipt".' },
        ],
      },
    ],
  },

  // ─────────────────────────────── Admin ───────────────────────────────
  {
    id: 'admin',
    title: 'Admin',
    purpose:
      'People, the program settings, the Steering Committee seats, notifications, the audit log, access reviews, the assistant and the Agreement §8 return package.',
    tasks: [
      {
        title: 'Invite someone',
        steps: [
          { t: 'In "People", click "Invite".' },
          {
            t: 'Enter the "Email", choose the "Role" and an optional "Title". For an executive, pick their "Steering Committee seat" and tick "Budget access" if they should see the budget.',
          },
          {
            t: 'Click "Send invitation". Without email set up, copy the link from the share dialog and send it yourself.',
            d: 'Links work once and expire after 7 days; "New link" issues a fresh one.',
          },
          { t: 'Send the link.', where: 'outside' },
        ],
      },
      {
        title: 'Link the default approver',
        steps: [
          { t: 'Open "Steering Committee".' },
          {
            t: 'On the approver seat (the President by default), choose their login under "HELM login".',
            d: 'Deliverables cannot be accepted, and no review notification is sent, until this is linked or a deliverable names a delegate.',
          },
        ],
      },
      {
        title: 'Run the quarterly access review',
        steps: [
          { t: 'Open "Access review".' },
          { t: 'Mark each person "Keep" or "Remove" (removals need a reason) and add "Notes".' },
          { t: 'Click the complete button. Removed people are signed out at once.' },
        ],
      },
      {
        title: 'Record the AI approval',
        steps: [
          { t: 'Get {client}’s signed letter approving the assistant, naming the tiers.', where: 'outside' },
          { t: 'File the letter in Documents.' },
          {
            t: 'In "Assistant", click "Record the signed approval", tick the "Tiers approved", fill in "Signed by", "Date signed" and "Signed letter", then "Record approval".',
            d: 'Leave client roles off unless the letter allows them. "Revoke" turns every assistant control off at once.',
          },
        ],
      },
      {
        title: 'Restore something deleted',
        steps: [
          {
            t: 'Open "Recently deleted".',
            d: 'It lists documents, tasks, RAID items, meetings and vendors deleted in the last 30 days, with who deleted each and when.',
          },
          { t: 'Click "Restore" beside the item. It goes back where it was.' },
        ],
      },
      {
        title: 'Send notifications by hand',
        steps: [
          {
            t: 'With email off, open "Notifications" and use "Copy link" to share each one.',
            d: 'This includes people mentioned in comments. Notifications are links only; they never carry report content or the comment text.',
          },
        ],
      },
      {
        title: 'Return and destroy {client}’s information',
        steps: [
          { t: 'In "Export and purge", click "Build export" and download the return package when ready.' },
          { t: 'Deliver the package to {client} and get their request for destruction.', where: 'outside' },
          {
            t: 'Click "Destroy and issue a certificate", name the requester, type the program name and a fresh authenticator code, then "Destroy".',
            d: 'Irreversible. It needs a return package from the last 7 days.',
          },
        ],
      },
    ],
  },

  // ─────────────────────────────── Account ───────────────────────────────
  {
    id: 'account',
    title: 'Your account',
    purpose:
      'Your password, two-factor authentication and recovery codes. Open it from your initials at the top right, then "Account and security".',
    tasks: [
      {
        title: 'Change your password',
        steps: [
          {
            t: 'Enter your "Current password", the "New password" twice, then "Change password".',
            d: 'At least 12 characters, not containing your email name. Other sessions are signed out.',
          },
        ],
      },
      {
        title: 'Set up two-factor authentication',
        steps: [
          { t: 'Install an authenticator app on your phone.', where: 'outside' },
          { t: 'Click "Set up", scan the code, type the 6-digit code, then "Turn on two-factor authentication".' },
          { t: 'Save the recovery codes somewhere safe, tick the box, then "Continue to HELM".' },
        ],
      },
      {
        title: 'Replace your recovery codes',
        steps: [
          {
            t: 'Enter an "Authenticator code" and click "Generate new codes".',
            d: 'The old codes stop working. Save the new ones before closing.',
          },
        ],
      },
      {
        title: 'If you forget your password',
        roles: CLIENT,
        steps: [{ t: 'Ask BlackWolf for a reset link. It works once, for 30 minutes.', where: 'outside' }],
      },
    ],
  },
];
